import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  doc, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { CustomRubric } from '../types';
import { useAuth } from './useAuth';

interface UseCustomRubricsReturn {
  rubrics: CustomRubric[];
  loading: boolean;
  error: string | null;
  activeRubricId: string | null;
  setActiveRubricId: (id: string | null) => void;
  activeRubric: CustomRubric | null;
  createRubric: (rubric: Omit<CustomRubric, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateRubric: (id: string, updates: Partial<Omit<CustomRubric, 'id' | 'createdAt'>>) => Promise<void>;
  deleteRubric: (id: string) => Promise<void>;
}

export function useCustomRubrics(): UseCustomRubricsReturn {
  const { user } = useAuth();
  const [rubrics, setRubrics] = useState<CustomRubric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeRubricId, setActiveRubricIdInternal] = useState<string | null>(() => {
    try {
      const storedId = localStorage.getItem('ionoff-active-rubric-id');
      console.log('📋 [useCustomRubrics] Initial activeRubricId from localStorage:', storedId);
      return storedId;
    } catch {
      return null;
    }
  });

  // Track if user explicitly disabled custom review (to prevent auto-select from overriding)
  const [userExplicitlyDisabled, setUserExplicitlyDisabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem('ionoff-custom-review-disabled') === 'true';
    } catch {
      return false;
    }
  });

  // Wrapper to track explicit disable
  const setActiveRubricId = useCallback((id: string | null) => {
    console.log('📋 [useCustomRubrics] setActiveRubricId called with:', id);
    if (id === null) {
      // User is explicitly disabling custom review
      setUserExplicitlyDisabled(true);
      try {
        localStorage.setItem('ionoff-custom-review-disabled', 'true');
      } catch { /* ignore */ }
    } else {
      // User is enabling a rubric
      setUserExplicitlyDisabled(false);
      try {
        localStorage.removeItem('ionoff-custom-review-disabled');
      } catch { /* ignore */ }
    }
    setActiveRubricIdInternal(id);
  }, []);

  // Persist active rubric ID to localStorage
  useEffect(() => {
    console.log('📋 [useCustomRubrics] activeRubricId changed to:', activeRubricId);
    try {
      if (activeRubricId) {
        localStorage.setItem('ionoff-active-rubric-id', activeRubricId);
        console.log('📋 [useCustomRubrics] Saved to localStorage:', activeRubricId);
      } else {
        localStorage.removeItem('ionoff-active-rubric-id');
        console.log('📋 [useCustomRubrics] Removed from localStorage');
      }
    } catch {
      // Ignore localStorage errors
    }
  }, [activeRubricId]);

  // Subscribe to user's custom rubrics
  useEffect(() => {
    if (!user?.uid) {
      setRubrics([]);
      setLoading(false);
      return;
    }

    const rubricsRef = collection(db, 'users', user.uid, 'customRubrics');
    const q = query(rubricsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const loadedRubrics: CustomRubric[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        })) as CustomRubric[];
        
        setRubrics(loadedRubrics);
        setLoading(false);
        setError(null);
        console.log('📋 Custom rubrics loaded:', loadedRubrics.length);
      },
      (err) => {
        console.error('❌ Error loading custom rubrics:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Get the currently active rubric
  const activeRubric = activeRubricId 
    ? rubrics.find(r => r.id === activeRubricId) || null
    : null;

  // Track recently created rubric IDs to prevent premature cleanup
  const [pendingRubricId, setPendingRubricId] = useState<string | null>(null);

  // Auto-select most recent rubric if activeRubricId is null but rubrics exist
  // OR clear active rubric if it no longer exists
  useEffect(() => {
    console.log('📋 [useCustomRubrics] Cleanup/auto-select check:', { 
      activeRubricId, 
      rubricsLength: rubrics.length, 
      activeRubricFound: !!activeRubric,
      rubricIds: rubrics.map(r => r.id),
      pendingRubricId,
      userExplicitlyDisabled
    });
    
    // If the pending rubric is now in the collection, clear the pending flag
    if (pendingRubricId && rubrics.find(r => r.id === pendingRubricId)) {
      console.log('📋 [useCustomRubrics] Pending rubric now available:', pendingRubricId);
      setPendingRubricId(null);
    }
    
    // Auto-select the most recent rubric if none is active but rubrics exist
    // BUT: Don't auto-select if user explicitly disabled custom review
    if (!activeRubricId && rubrics.length > 0 && !userExplicitlyDisabled) {
      const mostRecentRubric = rubrics[0]; // Already sorted by createdAt desc
      console.log('📋 [useCustomRubrics] Auto-selecting most recent rubric:', mostRecentRubric.id);
      setActiveRubricIdInternal(mostRecentRubric.id);
      // Clear the disabled flag since we're selecting
      setUserExplicitlyDisabled(false);
      try {
        localStorage.removeItem('ionoff-custom-review-disabled');
      } catch { /* ignore */ }
    }
    // Clear active rubric if it no longer exists in the collection
    // BUT: Don't clear if it's a pending (just-created) rubric that hasn't loaded yet
    else if (activeRubricId && rubrics.length > 0 && !activeRubric && activeRubricId !== pendingRubricId) {
      console.log('⚠️ Active rubric no longer exists (and not pending), clearing');
      setActiveRubricIdInternal(null);
    }
  }, [activeRubricId, rubrics, activeRubric, pendingRubricId, userExplicitlyDisabled]);

  const createRubric = useCallback(async (
    rubricData: Omit<CustomRubric, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> => {
    if (!user?.uid) {
      throw new Error('Must be logged in to create a rubric');
    }

    // Filter out undefined values - Firestore doesn't accept them
    const cleanedData: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(rubricData)) {
      if (value !== undefined) {
        cleanedData[key] = value;
      }
    }

    const rubricsRef = collection(db, 'users', user.uid, 'customRubrics');
    const docRef = await addDoc(rubricsRef, {
      ...cleanedData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    console.log('✅ Created custom rubric:', docRef.id);
    
    // Mark this rubric as pending so the cleanup effect doesn't clear it
    // before the Firestore listener has a chance to load it
    setPendingRubricId(docRef.id);
    
    return docRef.id;
  }, [user?.uid]);

  const updateRubric = useCallback(async (
    id: string,
    updates: Partial<Omit<CustomRubric, 'id' | 'createdAt'>>
  ): Promise<void> => {
    if (!user?.uid) {
      throw new Error('Must be logged in to update a rubric');
    }

    const rubricRef = doc(db, 'users', user.uid, 'customRubrics', id);
    await updateDoc(rubricRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    console.log('✅ Updated custom rubric:', id);
  }, [user?.uid]);

  const deleteRubric = useCallback(async (id: string): Promise<void> => {
    if (!user?.uid) {
      throw new Error('Must be logged in to delete a rubric');
    }

    // Clear active if deleting active rubric
    if (activeRubricId === id) {
      setActiveRubricId(null);
    }

    const rubricRef = doc(db, 'users', user.uid, 'customRubrics', id);
    await deleteDoc(rubricRef);

    console.log('✅ Deleted custom rubric:', id);
  }, [user?.uid, activeRubricId]);

  return {
    rubrics,
    loading,
    error,
    activeRubricId,
    setActiveRubricId,
    activeRubric,
    createRubric,
    updateRubric,
    deleteRubric,
  };
}

